import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors} from "@angular/forms";
import moment, {Duration, unitOfTime} from "moment-timezone";
import {FormValues} from "@shared/util/angular/angular-utils";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {DurationDisplay, durationDisplaysByUnit} from "@shared/services/util/period-util.service";
import {ObjectUtils} from "@shared/util/general/object-utils";


interface DurationForm {
  year: FormControl<number | null>;
  month: FormControl<number | null>;
  week: FormControl<number | null>;
  day: FormControl<number | null>;
  hour: FormControl<number | null>;
  minute: FormControl<number | null>;
  second: FormControl<number | null>;
}

type DurationFormValue = FormValues<DurationForm>;
export type DurationFormUnit = keyof DurationForm;

const emptyFormValue: DurationFormValue = {
  year: null,
  month: null,
  week: null,
  day: null,
  hour: null,
  minute: null,
  second: null
};

export interface DurationInputOptions {
  showLabel?: boolean;
  clearable?: boolean;
  units?: DurationFormUnit[];
}


interface FormDurationDisplay extends DurationDisplay {
  formUnit: DurationFormUnit;
}


@Component({
  selector: 'app-duration-input',
  templateUrl: './duration-input.component.html',
  styleUrl: './duration-input.component.scss',
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DurationInputComponent), multi: true}],
  standalone: false
})
export class DurationInputComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, DurationInputOptions {
  private readonly ngUnsubscribe = new Subject<void>();

  @Input() showLabel = false;
  @Input() clearable = false;
  @Input() units: DurationFormUnit[] = ['hour', 'minute'];

  protected durationForm: FormGroup<DurationForm>;
  protected formDurationDisplays: FormDurationDisplay[];

  constructor() {
    this.durationForm = new FormGroup({
      year: new FormControl<number | null>(null),
      month: new FormControl<number | null>(null),
      week: new FormControl<number | null>(null),
      day: new FormControl<number | null>(null),
      hour: new FormControl<number | null>(null),
      minute: new FormControl<number | null>(null),
      second: new FormControl<number | null>(null)
    }, {
      validators: [this.durationInputsValidator]
    });
    this.durationForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(values => {
      let duration = moment.duration(values);
      duration = duration.asMilliseconds() > 0 ? duration : null;
      this.onChange(duration);
      this.onTouched();
    });
  }

  ngOnInit(): void {
    if (!this.formDurationDisplays) {
      this.updateDisplayUnits();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.units) {
      this.updateDisplayUnits();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private durationInputsValidator = (form: AbstractControl): ValidationErrors | null => {
    const formValues = form.value;
    const error = {};
    Object.entries(formValues).forEach(([unit, unitValue]: [unitOfTime.Base, number | null]) => {
      if (unitValue) {
        if (unitValue < 0) {
          error[unit] = {
            messageKey: "unitValue cannot be negative",
            parameters: {
              unit: durationDisplaysByUnit.get(unit).label
            }
          }
        } else if (Math.round(unitValue) !== unitValue) {
          error[unit] = {
            messageKey: "unitValue must be integer",
            parameters: {
              unit: durationDisplaysByUnit.get(unit).label
            }
          }
        }
      }
    });
    return ObjectUtils.isEmpty(error) ? null : error;
  };

  private updateDisplayUnits() {
    if (!this.units?.length) {
      throw new Error('"units" cannot be empty in ' + DurationInputComponent.name);
    }
    this.formDurationDisplays = this.units.map(unit => {
      const durationDisplay = durationDisplaysByUnit.get(unit);
      if (!durationDisplay) {
        throw new Error('Unsupported duration unit ' + unit);
      }
      return {
        formUnit: unit,
        ...durationDisplay
      };
    })
  }

  private onChange = (_: any) => {
  };
  private onTouched = () => {
  };

  writeValue(duration: Duration): void {
    if (duration) {
      const durationClone = duration.clone();
      const formValue = {...emptyFormValue};
      this.units.forEach(unit => {
        const unitValue = Math.floor(durationClone.as(unit));
        if (unitValue) {
          formValue[unit] = unitValue;
          durationClone.subtract(unitValue, unit);
        }
      });
      this.durationForm.setValue(formValue);
    } else {
      this.durationForm.setValue(emptyFormValue);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.durationForm.disable();
    } else {
      this.durationForm.enable();
    }
  }

  clearDuration() {
    this.durationForm.setValue(emptyFormValue);
  }
}
